
import DOMPurify from 'dompurify';

/**
 * Security utilities for input validation and sanitization
 */

// Input validation patterns
const VALIDATION_PATTERNS = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  name: /^[a-zA-ZÀ-ÿ\s'-]{1,100}$/,
  productName: /^[a-zA-ZÀ-ÿ0-9\s\-_.,()]{1,200}$/,
  quantity: /^\d+(?:[.,]\d+)?$/,
  unit: /^[a-zA-Z]{1,10}$/,
  shopName: /^[a-zA-ZÀ-ÿ0-9\s\-_.,()]{1,100}$/,
  address: /^[a-zA-ZÀ-ÿ0-9\s\-_.,()]{1,500}$/,
  barcode: /^[0-9]{8,13}$/
};

// Sanitization options
const SANITIZE_OPTIONS = {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong'],
  ALLOWED_ATTR: [],
  KEEP_CONTENT: true
};

/**
 * Sanitize HTML content to prevent XSS attacks
 */
export function sanitizeHtml(input: string): string {
  if (!input || typeof input !== 'string') return '';
  return DOMPurify.sanitize(input, SANITIZE_OPTIONS);
}

/**
 * Sanitize text input by removing potentially dangerous characters
 */
export function sanitizeText(input: string): string {
  if (!input || typeof input !== 'string') return '';

  // Remove HTML tags and special characters that could be used for injection
  return input
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/[<>'"&]/g, '') // Remove dangerous characters
    .trim()
    .substring(0, 1000); // Limit length
}

/**
 * Sanitize URL input by removing HTML tags while preserving common URL characters
 */
export function sanitizeUrl(input: string): string {
  if (!input || typeof input !== 'string') return '';

  return input
    .replace(/<[^>]*>/g, '') // Strip HTML tags
    .replace(/[<>"']/g, '') // Remove angle brackets and quotes
    .trim()
    .substring(0, 1000);
}

/**
 * Validate and sanitize product name
 */
export function validateProductName(name: string): { isValid: boolean; sanitized: string; error?: string } {
  if (!name || typeof name !== 'string') {
    return { isValid: false, sanitized: '', error: 'Nome prodotto richiesto' };
  }

  const sanitized = sanitizeText(name);
  
  if (sanitized.length < 1) {
    return { isValid: false, sanitized: '', error: 'Nome prodotto troppo corto' };
  }
  
  if (sanitized.length > 200) {
    return { isValid: false, sanitized: '', error: 'Nome prodotto troppo lungo (max 200 caratteri)' };
  }
  
  if (!VALIDATION_PATTERNS.productName.test(sanitized)) {
    return { isValid: false, sanitized: '', error: 'Nome prodotto contiene caratteri non validi' };
  }

  return { isValid: true, sanitized };
}

/**
 * Validate and sanitize email
 */
export function validateEmail(email: string): { isValid: boolean; sanitized: string; error?: string } {
  if (!email || typeof email !== 'string') {
    return { isValid: false, sanitized: '', error: 'Email richiesta' };
  }

  const sanitized = email.toLowerCase().trim();
  
  if (!VALIDATION_PATTERNS.email.test(sanitized)) {
    return { isValid: false, sanitized: '', error: 'Formato email non valido' };
  }

  return { isValid: true, sanitized };
}

/**
 * Validate password strength following Supabase policy
 */
export function validatePassword(password: string): { isValid: boolean; error?: string } {
  if (!password || typeof password !== 'string') {
    return { isValid: false, error: 'Password richiesta' };
  }

  const strongPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]).{8,}$/;
  if (!strongPattern.test(password)) {
    return {
      isValid: false,
      error:
        'Password non sufficientemente complessa. Deve contenere almeno 8 caratteri, una maiuscola, una minuscola, un numero e un simbolo.'
    };
  }

  if (password.length > 128) {
    return { isValid: false, error: 'Password troppo lunga' };
  }

  return { isValid: true };
}

/**
 * Validate and sanitize quantity
 */
export function validateQuantity(quantity: string | number): { isValid: boolean; sanitized: string; error?: string } {
  if (quantity === null || quantity === undefined) {
    return { isValid: false, sanitized: '', error: 'Quantità non valida' };
  }

  const str = String(quantity).trim();

  if (!str) {
    return { isValid: false, sanitized: '', error: 'Quantità non valida' };
  }

  if (!VALIDATION_PATTERNS.quantity.test(str)) {
    return { isValid: false, sanitized: '', error: 'Quantità non valida' };
  }

  const normalized = str.replace(/,/g, '.');
  const decimalCount = (normalized.match(/\./g) || []).length;

  if (decimalCount > 1) {
    return { isValid: false, sanitized: '', error: 'Quantità non valida' };
  }

  const num = Number.parseFloat(normalized);

  if (!Number.isFinite(num) || num < 0.1 || num > 9999) {
    return { isValid: false, sanitized: '', error: 'Quantità deve essere tra 0.1 e 9999' };
  }

  return { isValid: true, sanitized: num.toString() };
}

/**
 * Validate and sanitize shop name
 */
export function validateShopName(name: string): { isValid: boolean; sanitized: string; error?: string } {
  if (!name || typeof name !== 'string') {
    return { isValid: false, sanitized: '', error: 'Nome negozio richiesto' };
  }

  const sanitized = sanitizeText(name);
  
  if (sanitized.length < 1) {
    return { isValid: false, sanitized: '', error: 'Nome negozio troppo corto' };
  }
  
  if (sanitized.length > 100) {
    return { isValid: false, sanitized: '', error: 'Nome negozio troppo lungo (max 100 caratteri)' };
  }

  return { isValid: true, sanitized };
}

/**
 * Validate barcode
 */
export function validateBarcode(barcode: string): { isValid: boolean; sanitized: string; error?: string } {
  if (!barcode || typeof barcode !== 'string') {
    return { isValid: false, sanitized: '', error: 'Codice a barre richiesto' };
  }

  const sanitized = barcode.trim();
  
  if (!VALIDATION_PATTERNS.barcode.test(sanitized)) {
    return { isValid: false, sanitized: '', error: 'Formato codice a barre non valido (8-13 cifre)' };
  }

  return { isValid: true, sanitized };
}

/**
 * Sanitize data before storing in localStorage
 */
export function sanitizeForStorage(data: any): any {
  if (typeof data === 'string') {
    return sanitizeText(data);
  }
  
  if (Array.isArray(data)) {
    return data.map(item => sanitizeForStorage(item));
  }
  
  if (data && typeof data === 'object') {
    const sanitized: any = {};
    Object.keys(data).forEach(key => {
      const sanitizedKey = sanitizeText(key);
      if (sanitizedKey) {
        sanitized[sanitizedKey] = sanitizeForStorage(data[key]);
      }
    });
    return sanitized;
  }
  
  return data;
}

/**
 * Rate limiting utility
 */
class RateLimiter {
  private attempts: Map<string, { count: number; resetTime: number }> = new Map();
  
  isAllowed(key: string, maxAttempts: number, windowMs: number): boolean {
    const now = Date.now();
    const record = this.attempts.get(key);
    
    if (!record || now > record.resetTime) {
      this.attempts.set(key, { count: 1, resetTime: now + windowMs });
      return true;
    }
    
    if (record.count >= maxAttempts) {
      return false;
    }
    
    record.count++;
    return true;
  }
  
  getRemainingTime(key: string): number {
    const record = this.attempts.get(key);
    if (!record) return 0;
    
    const remaining = record.resetTime - Date.now();
    return Math.max(0, remaining);
  }
}

export const rateLimiter = new RateLimiter();
